import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Baby, Check, Download, Eye, FileText, Landmark, Loader2, Pencil, UsersRound, X } from "lucide-react";
import { z } from "zod";

import {
  Button,
  DatePicker,
  DatePickerFieldTrigger,
  ensureV2DatepickerPortal,
  FileField,
  Modal,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  TextArea,
  TextField,
} from "../../../ui/index.ts";
import { API_EMPLOYEE_INSURANCE } from "../../../../../config/Endpoints.js";
import { getAuthToken } from "../../../../../utils/Storage.js";
import { ApiError, getApi, postFormDataMultipart } from "../../../../utils/api.ts";
import { showError, showSuccess } from "../../../../utils/toast.ts";
import { useUser } from "../../../../lib/rbac/useUser.ts";
import { useSqeLabs } from "../../../../hooks/use-sqe-labs.ts";
import { SQE_PHASE2, unwrapSqeData } from "../../onboarding/sqe/sqe-onboarding-api.ts";
import type { Phase2ListRow } from "../schemas/phase2-list.schema.ts";
import { chequeFileNameFromPath } from "../utils/cancelled-cheque-filepath.ts";
import {
  formatDocTypeLabel,
  isDocumentPending,
  normalizeDocumentStatus,
} from "../utils/doc-type-label.ts";
import DocumentViewer from "../../../../../components/DocumentViewer.jsx";

import styles from "./employee-verification-modal.module.css";

const TAB_OVERVIEW = "overview";
const TAB_DOCUMENTS = "documents";
const TAB_BANK = "bank";
const TAB_INSURANCE = "insurance";

const MAX_INSURANCE_FILE_SIZE = 5 * 1024 * 1024;
const INSURANCE_FILE_TYPES = ["application/pdf", "image/jpeg", "image/png"];
const FIXED_INSURANCE_PROVIDER = "HDFC ERGO";
const INSURANCE_NUMBER_PATTERN = /^[A-Z]{2}\d{10}-\d{2}[A-Z]$/;
const DEFAULT_INSURANCE_FROM = "2025-11-17";
const DEFAULT_INSURANCE_TO = "2026-11-16";

const insuranceSchema = z
  .object({
    insuranceProviderName: z.literal(FIXED_INSURANCE_PROVIDER),
    insuranceNumber: z
      .string()
      .trim()
      .min(1, "Insurance number is required")
      .regex(
        INSURANCE_NUMBER_PATTERN,
        "Use format EC2426456447-01E",
      ),
    insuranceFrom: z.string().min(1, "Valid from date is required"),
    insuranceTo: z.string().trim().optional(),
  })
  .refine(
    (value) =>
      !value.insuranceTo || value.insuranceTo >= value.insuranceFrom,
    {
      message: "Valid to date must be on or after valid from date",
      path: ["insuranceTo"],
    },
  );

function validateDependentInsurance(
  prefix: "spouse" | "child",
  label: string,
  number: string,
  from: string,
  to: string,
  file: File | null,
  errors: Record<string, string>,
) {
  const numberKey = `${prefix}InsuranceNumber`;
  const fromKey = `${prefix}InsuranceFrom`;
  const toKey = `${prefix}InsuranceTo`;
  const fileKey = `${prefix}InsuranceSoftCopy`;
  if (!number.trim()) errors[numberKey] = `${label} insurance number is required`;
  else if (!INSURANCE_NUMBER_PATTERN.test(number.trim())) errors[numberKey] = "Use format EC2426456447-01E";
  if (!from) errors[fromKey] = `${label} valid from date is required`;
  if (to && from && to < from) errors[toKey] = "Valid till date must be on or after valid from date";
  if (file && !INSURANCE_FILE_TYPES.includes(file.type)) errors[fileKey] = "Upload a PDF, JPG, JPEG, or PNG file";
  else if (file && file.size > MAX_INSURANCE_FILE_SIZE) errors[fileKey] = "File size must not exceed 5 MB";
}

function localIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseIsoDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

function familyInsuranceDocumentPaths(
  path: string | undefined,
  employeeId: number,
): string[] {
  if (!path) return [];
  const prefix = `${employeeId}_insaurance_`;
  if (path.startsWith(prefix)) {
    return [`${employeeId}/insaurance/${path.slice(prefix.length)}`, path];
  }
  return [path];
}

type ConfirmAction =
  | "approveAllDocs"
  | "rejectAllDocs"
  | "approveBank"
  | "rejectBank"
  | null;

type DocResultState = "success" | "failed" | null;

export type EmployeeVerificationModalProps = {
  open: boolean;
  onClose: () => void;
  row: Phase2ListRow | null;
  busy?: boolean;
  onApproveBank: (employeeId: number) => Promise<void>;
  onRejectBank: (employeeId: number, comment: string) => Promise<void>;
  onApproveDocument: (documentId: number) => Promise<void>;
  onRejectDocument: (documentId: number, reason: string) => Promise<void>;
  onBatchComplete?: () => void | Promise<void>;
  fetchChequeBlob: (row: Phase2ListRow, signal?: AbortSignal) => Promise<Blob>;
  fetchFileBlob: (filepath: string, signal?: AbortSignal) => Promise<Blob>;
};

function formatSubmitted(iso: string | undefined): string {
  if (iso == null || iso === "") return "—";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function formatDateOnly(iso: string | undefined): string {
  if (iso == null || iso === "") return "—";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { dateStyle: "medium" });
}

function summaryStatusLabel(
  kind: "bank" | "document",
  status: string | undefined,
): string {
  const u = normalizeDocumentStatus(status);
  if (kind === "bank") {
    if (u === "APPROVED") return "Approved";
    if (u === "REJECTED") return "Rejected";
    if (u === "PENDING" || u === "SUBMITTED") return "Submitted";
    return "Not submitted";
  }
  if (u === "APPROVED") return "Uploaded";
  if (u === "REJECTED") return "Rejected";
  return "Uploaded";
}

function extractErrorMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  return "Request failed.";
}

export function EmployeeVerificationModal({
  open,
  onClose,
  row,
  busy = false,
  onApproveBank,
  onRejectBank,
  onApproveDocument,
  onRejectDocument,
  onBatchComplete,
  fetchChequeBlob,
  fetchFileBlob,
}: EmployeeVerificationModalProps) {
  const isSqe = useSqeLabs();
  const user = useUser();
  const normalizedRoles = user?.roles.map((role) =>
    String(role).trim().toUpperCase(),
  ) ?? [];
  const hasAdminRole = normalizedRoles.includes("ADMIN");
  const hasSuperAdminRole = normalizedRoles.includes("SUPERADMIN");
  const hasHrRole = normalizedRoles.includes("HR");
  const canManageInsurance = isSqe ? hasHrRole || hasSuperAdminRole : hasAdminRole || hasSuperAdminRole;
  const showInsuranceTab =
    canManageInsurance &&
    String(row?.employeeShift ?? "").trim().toUpperCase() !== "CCI_US";
  const insuranceOnlyView =
    showInsuranceTab && hasAdminRole && !hasSuperAdminRole && !hasHrRole;
  const [activeTab, setActiveTab] = useState(
    insuranceOnlyView ? TAB_INSURANCE : TAB_OVERVIEW,
  );
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [docStatuses, setDocStatuses] = useState<Record<string, string>>({});
  const [docResults, setDocResults] = useState<Record<string, DocResultState>>({});
  const [batchBusy, setBatchBusy] = useState(false);
  const [loadingDocId, setLoadingDocId] = useState<string | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [viewerType, setViewerType] = useState("application/octet-stream");
  const [viewerName, setViewerName] = useState("Document");
  const [chequeLoading, setChequeLoading] = useState(false);
  const insuranceProviderName = FIXED_INSURANCE_PROVIDER;
  const [insuranceNumber, setInsuranceNumber] = useState("");
  const [insuranceFrom, setInsuranceFrom] = useState(DEFAULT_INSURANCE_FROM);
  const [insuranceTo, setInsuranceTo] = useState(DEFAULT_INSURANCE_TO);
  const [insuranceSoftCopy, setInsuranceSoftCopy] = useState<File | null>(null);
  const [spouseInsuranceNumber, setSpouseInsuranceNumber] = useState("");
  const [spouseInsuranceFrom, setSpouseInsuranceFrom] = useState(DEFAULT_INSURANCE_FROM);
  const [spouseInsuranceTo, setSpouseInsuranceTo] = useState(DEFAULT_INSURANCE_TO);
  const [spouseInsuranceSoftCopy, setSpouseInsuranceSoftCopy] = useState<File | null>(null);
  const [childInsuranceNumber, setChildInsuranceNumber] = useState("");
  const [childInsuranceFrom, setChildInsuranceFrom] = useState(DEFAULT_INSURANCE_FROM);
  const [childInsuranceTo, setChildInsuranceTo] = useState(DEFAULT_INSURANCE_TO);
  const [childInsuranceSoftCopy, setChildInsuranceSoftCopy] = useState<File | null>(null);
  const [insuranceErrors, setInsuranceErrors] = useState<Record<string, string>>({});
  const [insuranceBusy, setInsuranceBusy] = useState(false);

  useEffect(() => {
    ensureV2DatepickerPortal();
  }, []);

  useEffect(() => {
    if (!showInsuranceTab && activeTab === TAB_INSURANCE) {
      setActiveTab(TAB_OVERVIEW);
    }
  }, [activeTab, showInsuranceTab]);

  useEffect(() => {
    if (!open) {
      setActiveTab(insuranceOnlyView ? TAB_INSURANCE : TAB_OVERVIEW);
      setConfirmAction(null);
      setRejectReason("");
      setDocStatuses({});
      setDocResults({});
      setBatchBusy(false);
      setLoadingDocId(null);
      setViewerOpen(false);
      setViewerUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setInsuranceNumber("");
      setInsuranceFrom(DEFAULT_INSURANCE_FROM);
      setInsuranceTo(DEFAULT_INSURANCE_TO);
      setInsuranceSoftCopy(null);
      setSpouseInsuranceNumber("");
      setSpouseInsuranceFrom(DEFAULT_INSURANCE_FROM);
      setSpouseInsuranceTo(DEFAULT_INSURANCE_TO);
      setSpouseInsuranceSoftCopy(null);
      setChildInsuranceNumber("");
      setChildInsuranceFrom(DEFAULT_INSURANCE_FROM);
      setChildInsuranceTo(DEFAULT_INSURANCE_TO);
      setChildInsuranceSoftCopy(null);
      setInsuranceErrors({});
      setInsuranceBusy(false);
    }
  }, [insuranceOnlyView, open]);

  useEffect(() => {
    if (!open || row == null) return;
    const initial: Record<string, string> = {};
    for (const doc of row.documents) {
      initial[String(doc.documentId)] = doc.status ?? "";
    }
    setDocStatuses(initial);
    setDocResults({});
    const insuranceDetails = row.isInsuranceAvailable
      ? row.insuranceDetails
      : undefined;
    setInsuranceNumber(insuranceDetails?.insuranceNumber ?? "");
    setInsuranceFrom(insuranceDetails?.insuranceFrom ?? DEFAULT_INSURANCE_FROM);
    setInsuranceTo(insuranceDetails?.insuranceTo ?? DEFAULT_INSURANCE_TO);
    setInsuranceSoftCopy(null);
    setSpouseInsuranceNumber(row.familyInsuranceDetails?.spouseInsurance?.insuranceNumber ?? "");
    setSpouseInsuranceFrom(row.familyInsuranceDetails?.spouseInsurance?.insuranceFrom ?? DEFAULT_INSURANCE_FROM);
    setSpouseInsuranceTo(row.familyInsuranceDetails?.spouseInsurance?.insuranceTo ?? DEFAULT_INSURANCE_TO);
    setSpouseInsuranceSoftCopy(null);
    setChildInsuranceNumber(row.familyInsuranceDetails?.childInsurance?.insuranceNumber ?? "");
    setChildInsuranceFrom(row.familyInsuranceDetails?.childInsurance?.insuranceFrom ?? DEFAULT_INSURANCE_FROM);
    setChildInsuranceTo(row.familyInsuranceDetails?.childInsurance?.insuranceTo ?? DEFAULT_INSURANCE_TO);
    setChildInsuranceSoftCopy(null);
    setInsuranceErrors({});
  }, [open, row]);

  useEffect(() => {
    if (!isSqe || !open || !row?.isInsuranceAvailable) return;
    const controller = new AbortController();
    void getApi<unknown>(`${SQE_PHASE2}/insurance?employeeId=${encodeURIComponent(String(row.employeeId))}`, getAuthToken("accessToken") ?? undefined, "json", controller.signal)
      .then(({ data }) => {
        if (controller.signal.aborted) return;
        const body = unwrapSqeData(data);
        if (!body || typeof body !== "object") return;
        const record = body as Record<string, unknown>;
        const insurance = record.insuranceDetails && typeof record.insuranceDetails === "object"
          ? record.insuranceDetails as Record<string, unknown> : record;
        if (typeof insurance.insuranceNumber === "string") setInsuranceNumber(insurance.insuranceNumber);
        if (typeof insurance.insuranceFrom === "string") setInsuranceFrom(insurance.insuranceFrom);
        if (typeof insurance.insuranceTo === "string") setInsuranceTo(insurance.insuranceTo);
      })
      .catch(() => { /* Keep the insurance values provided by the list response. */ });
    return () => controller.abort();
  }, [isSqe, open, row?.employeeId, row?.isInsuranceAvailable]);

  const documents = useMemo(() => {
    if (row == null) return [];
    return row.documents.map((doc) => ({
      ...doc,
      status: docStatuses[String(doc.documentId)] ?? doc.status ?? "",
    }));
  }, [row, docStatuses]);

  const pendingDocuments = useMemo(
    () => documents.filter((doc) => isDocumentPending(doc.status)),
    [documents],
  );

  const documentsForTab = documents.filter((doc) =>
    isDocumentPending(doc.status) || normalizeDocumentStatus(doc.status) === "REJECTED",
  );

  const bankActionable = useMemo(() => {
    if (row == null || !row.isBankDetailsAvailable) return false;
    const s = (
      row.bankDetails?.approvalStatus ?? row.bankStatus ?? row.approvalStatus ?? ""
    ).toUpperCase();
    return s === "PENDING" || s === "SUBMITTED";
  }, [row]);

  const openFile = useCallback(
    async (docId: string, filepaths: string | string[], fileName: string) => {
      setLoadingDocId(docId);
      try {
        const paths = Array.isArray(filepaths) ? filepaths : [filepaths];
        let blob: Blob | null = null;
        let lastError: unknown;
        for (const filepath of paths) {
          try {
            blob = await fetchFileBlob(filepath);
            break;
          } catch (error) {
            lastError = error;
          }
        }
        if (!blob) throw lastError ?? new Error("Unable to load document.");
        const url = URL.createObjectURL(blob);
        setViewerUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
        setViewerType(blob.type || "application/octet-stream");
        setViewerName(fileName);
        setViewerOpen(true);
      } catch (e) {
        showError(extractErrorMessage(e));
      } finally {
        setLoadingDocId(null);
      }
    },
    [fetchFileBlob],
  );

  const openCheque = useCallback(async () => {
    if (!row?.bankDetails.cancelledChequePath) return;
    setChequeLoading(true);
    try {
      const blob = await fetchChequeBlob(row);
      const url = URL.createObjectURL(blob);
      setViewerUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
      setViewerType(blob.type || "application/octet-stream");
      setViewerName(
        chequeFileNameFromPath(row.bankDetails.cancelledChequePath),
      );
      setViewerOpen(true);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setChequeLoading(false);
    }
  }, [row, fetchChequeBlob]);

  useEffect(() => {
    if (!viewerOpen) return;
    const element = document.getElementById("staticBackdropDocumentViewer");
    if (element) {
      const modal = new window.bootstrap.Modal(element);
      modal.show();
    }
  }, [viewerOpen]);

  const closeViewer = useCallback(() => {
    setViewerOpen(false);
    setViewerUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const runBatchApprove = useCallback(async () => {
    const targets = pendingDocuments;
    if (targets.length === 0) {
      showError("No pending documents to approve.");
      return;
    }

    setBatchBusy(true);
    setDocResults({});
    let successCount = 0;
    let failCount = 0;

    for (const doc of targets) {
      const id = String(doc.documentId);
      try {
        await onApproveDocument(Number(doc.documentId));
        setDocStatuses((prev) => ({ ...prev, [id]: "approved" }));
        setDocResults((prev) => ({ ...prev, [id]: "success" }));
        successCount += 1;
      } catch (e) {
        setDocResults((prev) => ({ ...prev, [id]: "failed" }));
        failCount += 1;
        showError(extractErrorMessage(e));
      }
    }

    setBatchBusy(false);
    setConfirmAction(null);

    if (successCount > 0) {
      showSuccess(
        successCount === 1
          ? "1 document approved successfully."
          : `${successCount} documents approved successfully.`,
      );
    }
    if (failCount > 0 && successCount === 0) {
      showError("Failed to approve documents.");
    }
    await onBatchComplete?.();
  }, [pendingDocuments, onApproveDocument, onBatchComplete]);

  const runBatchReject = useCallback(async () => {
    const reason = rejectReason.trim();
    if (!reason) {
      showError("Comment is required to reject documents.");
      return;
    }

    const targets = pendingDocuments;
    if (targets.length === 0) {
      showError("No pending documents to reject.");
      return;
    }

    setBatchBusy(true);
    setDocResults({});
    let successCount = 0;
    let failCount = 0;

    for (const doc of targets) {
      const id = String(doc.documentId);
      try {
        await onRejectDocument(Number(doc.documentId), reason);
        setDocStatuses((prev) => ({ ...prev, [id]: "rejected" }));
        setDocResults((prev) => ({ ...prev, [id]: "success" }));
        successCount += 1;
      } catch (e) {
        setDocResults((prev) => ({ ...prev, [id]: "failed" }));
        failCount += 1;
        showError(extractErrorMessage(e));
      }
    }

    setBatchBusy(false);
    setConfirmAction(null);
    setRejectReason("");

    if (successCount > 0) {
      showSuccess(
        successCount === 1
          ? "1 document rejected successfully."
          : `${successCount} documents rejected successfully.`,
      );
    }
    if (failCount > 0 && successCount === 0) {
      showError("Failed to reject documents.");
    }
    await onBatchComplete?.();
  }, [rejectReason, pendingDocuments, onRejectDocument, onBatchComplete]);

  const handleApproveBank = useCallback(async () => {
    if (!row) return;
    setBatchBusy(true);
    try {
      await onApproveBank(row.employeeId);
      showSuccess("Bank details approved.");
      setConfirmAction(null);
      onClose();
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setBatchBusy(false);
    }
  }, [row, onApproveBank, onClose]);

  const handleRejectBank = useCallback(async () => {
    if (!row) return;
    const reason = rejectReason.trim();
    if (!reason) {
      showError("Comment is required to reject.");
      return;
    }
    setBatchBusy(true);
    try {
      await onRejectBank(row.employeeId, reason);
      showSuccess("Bank details rejected.");
      setConfirmAction(null);
      setRejectReason("");
      onClose();
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setBatchBusy(false);
    }
  }, [row, rejectReason, onRejectBank, onClose]);

  const handleInsuranceSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!row || !canManageInsurance || row.isInsuranceAvailable) return;

      const parsed = insuranceSchema.safeParse({
        insuranceProviderName,
        insuranceNumber,
        insuranceFrom,
        insuranceTo,
      });
      const nextErrors: Record<string, string> = {};
      if (isSqe && !insuranceTo) nextErrors.insuranceTo = "Valid to date is required";
      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          const key = issue.path[0];
          if (typeof key === "string" && !nextErrors[key]) {
            nextErrors[key] = issue.message;
          }
        }
      }
      if (insuranceSoftCopy) {
        if (!INSURANCE_FILE_TYPES.includes(insuranceSoftCopy.type)) {
          nextErrors.insuranceSoftCopy = "Upload a PDF, JPG, JPEG, or PNG file";
        } else if (insuranceSoftCopy.size > MAX_INSURANCE_FILE_SIZE) {
          nextErrors.insuranceSoftCopy = "File size must not exceed 5 MB";
        }
      }
      if (String(row.maritalStatus ?? "").trim().toLowerCase() === "married") {
        validateDependentInsurance("spouse", "Spouse", spouseInsuranceNumber, spouseInsuranceFrom, spouseInsuranceTo, spouseInsuranceSoftCopy, nextErrors);
      }
      if (
        String(row.maritalStatus ?? "").trim().toLowerCase() === "married" &&
        row.childStatus
      ) {
        validateDependentInsurance("child", "Child", childInsuranceNumber, childInsuranceFrom, childInsuranceTo, childInsuranceSoftCopy, nextErrors);
      }
      setInsuranceErrors(nextErrors);
      if (!parsed.success || Object.keys(nextErrors).length > 0) return;

      setInsuranceBusy(true);
      try {
        const formData = new FormData();
        formData.append("employeeId", String(row.employeeId));
        formData.append("insuranceProviderName", parsed.data.insuranceProviderName);
        formData.append("insuranceNumber", parsed.data.insuranceNumber);
        formData.append("insuranceFrom", parsed.data.insuranceFrom);
        if (parsed.data.insuranceTo) {
          formData.append("insuranceTo", parsed.data.insuranceTo);
        }
        if (insuranceSoftCopy) {
          formData.append("insuranceSoftCopy", insuranceSoftCopy);
        }
        if (String(row.maritalStatus ?? "").trim().toLowerCase() === "married") {
          formData.append("spouseInsuranceNumber", spouseInsuranceNumber.trim());
          formData.append("spouseInsuranceFrom", spouseInsuranceFrom);
          formData.append("spouseInsuranceTo", spouseInsuranceTo);
          if (spouseInsuranceSoftCopy) {
            formData.append("spouseInsuranceSoftCopy", spouseInsuranceSoftCopy);
          }
        }
        if (String(row.maritalStatus ?? "").trim().toLowerCase() === "married") {
          formData.append("childInsuranceNumber", childInsuranceNumber.trim());
          formData.append("childInsuranceFrom", childInsuranceFrom);
          formData.append("childInsuranceTo", childInsuranceTo);
          if (childInsuranceSoftCopy) {
            formData.append("childInsuranceSoftCopy", childInsuranceSoftCopy);
          }
        }
        await postFormDataMultipart(
          isSqe ? `${SQE_PHASE2}/insurance` : API_EMPLOYEE_INSURANCE,
          getAuthToken("accessToken") ?? undefined,
          formData,
        );
        showSuccess("Insurance details uploaded successfully.");
        await onBatchComplete?.();
        onClose();
      } catch (error) {
        showError(
          error instanceof ApiError
            ? error.message
            : "Failed to upload insurance details.",
        );
      } finally {
        setInsuranceBusy(false);
      }
    },
    [
      row,
      isSqe,
      canManageInsurance,
      insuranceProviderName,
      insuranceNumber,
      insuranceFrom,
      insuranceTo,
      insuranceSoftCopy,
      spouseInsuranceNumber,
      spouseInsuranceFrom,
      spouseInsuranceTo,
      spouseInsuranceSoftCopy,
      childInsuranceNumber,
      childInsuranceFrom,
      childInsuranceTo,
      childInsuranceSoftCopy,
      onBatchComplete,
      onClose,
    ],
  );

  const handleConfirmYes = useCallback(() => {
    if (confirmAction === "approveAllDocs") {
      void runBatchApprove();
      return;
    }
    if (confirmAction === "rejectAllDocs") {
      void runBatchReject();
      return;
    }
    if (confirmAction === "approveBank") {
      void handleApproveBank();
      return;
    }
    if (confirmAction === "rejectBank") {
      void handleRejectBank();
    }
  }, [
    confirmAction,
    runBatchApprove,
    runBatchReject,
    handleApproveBank,
    handleRejectBank,
  ]);

  if (row == null) return null;

  const b = row.bankDetails;
  const insuranceReadOnly = row.isInsuranceAvailable;
  const existingInsurancePath = row.isInsuranceAvailable
    ? row.insuranceDetails?.insuranceSoftCopy
    : undefined;
  const existingInsuranceFileName = existingInsurancePath
    ?.split(/[\\/]/)
    .filter(Boolean)
    .pop();
  const spouseInsurancePath = row.familyInsuranceDetails?.spouseInsurance?.insuranceSoftCopy ?? undefined;
  const spouseInsuranceFileName = spouseInsurancePath?.split(/[\\/]/).filter(Boolean).pop();
  const childInsurancePath = row.familyInsuranceDetails?.childInsurance?.insuranceSoftCopy ?? undefined;
  const childInsuranceFileName = childInsurancePath?.split(/[\\/]/).filter(Boolean).pop();
  const spouseInsuranceDocumentPaths = familyInsuranceDocumentPaths(spouseInsurancePath, row.employeeId);
  const childInsuranceDocumentPaths = familyInsuranceDocumentPaths(childInsurancePath, row.employeeId);
  const insuranceValidToMaxDate = new Date(
    new Date().getFullYear() + 50,
    11,
    31,
  );
  const summaryItems: Array<{
    key: string;
    label: string;
    status: string;
    icon: typeof FileText;
  }> = [];

  if (row.isBankDetailsAvailable) {
    summaryItems.push({
      key: "bank",
      label: "Bank Details",
      status: summaryStatusLabel(
        "bank",
        row.bankDetails?.approvalStatus ?? row.bankStatus,
      ),
      icon: Landmark,
    });
  }

  const docTypesSeen = new Set<string>();
  for (const doc of documents) {
    if (docTypesSeen.has(doc.docType)) continue;
    docTypesSeen.add(doc.docType);
    summaryItems.push({
      key: `doc-${doc.docType}`,
      label: formatDocTypeLabel(doc.docType),
      status: summaryStatusLabel("document", doc.status),
      icon: FileText,
    });
  }

  const footer = activeTab === TAB_INSURANCE && showInsuranceTab ? (
    <div className={styles.footer}>
      <Button
        type="button"
        variant="light-grey"
        disabled={insuranceBusy}
        onClick={onClose}
      >
        {insuranceReadOnly ? "Close" : "Cancel"}
      </Button>
      {!insuranceReadOnly ? (
        <Button
          type="submit"
          form="employee-insurance-form"
          variant="accent"
          disabled={insuranceBusy}
        >
          {insuranceBusy ? "Submitting…" : "Submit"}
        </Button>
      ) : null}
    </div>
  ) : bankActionable && activeTab === TAB_BANK ? (
      <div className={styles.footer}>
        <Button
          type="button"
          variant="success"
          disabled={busy || batchBusy}
          className={styles.approveBtn}
          onClick={() => setConfirmAction("approveBank")}
        >
          <Check size={16} aria-hidden />
          Approve
        </Button>
        <Button
          type="button"
          variant="danger"
          disabled={busy || batchBusy}
          className={styles.rejectBtn}
          onClick={() => setConfirmAction("rejectBank")}
        >
          <X size={16} aria-hidden />
          Reject
        </Button>
      </div>
    ) : undefined;

  const confirmTitle =
    confirmAction === "approveAllDocs"
      ? "Approve all documents"
      : confirmAction === "rejectAllDocs"
        ? "Reject all documents"
        : confirmAction === "approveBank"
          ? "Approve bank details"
          : confirmAction === "rejectBank"
            ? "Reject bank details"
            : "";

  const confirmMessage =
    confirmAction === "approveAllDocs"
      ? "Are you sure you want to approve all these documents?"
      : confirmAction === "rejectAllDocs"
        ? "Are you sure you want to reject all these documents?"
        : confirmAction === "approveBank"
          ? "Are you sure you want to approve bank details for this employee?"
          : confirmAction === "rejectBank"
            ? "Are you sure you want to reject bank details for this employee?"
            : "";

  const confirmIsApprove =
    confirmAction === "approveAllDocs" || confirmAction === "approveBank";

  const showRejectReason =
    confirmAction === "rejectAllDocs" || confirmAction === "rejectBank";

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={
          insuranceOnlyView
            ? "Employee Insurance Details"
            : "Employee Documents & Bank Details"
        }
        size="xl"
        className={styles.verificationModal}
        closeOnBackdropClick={!busy && !batchBusy && loadingDocId == null}
        closeOnEscape={!busy && !batchBusy && !insuranceBusy && loadingDocId == null}
        footer={footer}
      >
        <div className={styles.modalShell}>
          <div className={styles.employeeBanner}>
            <div className={styles.employeePrimary}>

              <div className={styles.employeePrimaryText}>
                <p className={styles.metaLabel}>
                  {row.employeeName}
                </p>
                <p className={styles.metaValue}>{row.email}</p>
              </div>
            </div>
            <div className={styles.metaBlock}>
              <p className={styles.metaLabel}>Employee Code</p>
              <p className={styles.metaValue}>{row.employeeCode}</p>
            </div>
            <div className={styles.metaBlock}>
              <p className={styles.metaLabel}>Designation</p>
              <p className={styles.metaValue}>{row.designation ?? "—"}</p>
            </div>
            <div className={styles.metaBlock}>
              <p className={styles.metaLabel}>Date of Joining</p>
              <p className={styles.metaValue}>
                {formatDateOnly(row.doj)}
              </p>
            </div>
          </div>

          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            idPrefix="ev-modal"
            className={styles.tabsRoot}
          >
            <TabsList>
              {insuranceOnlyView ? (
                <TabsTrigger value={TAB_INSURANCE}>Insurance Details</TabsTrigger>
              ) : (
                <>
                  <TabsTrigger value={TAB_OVERVIEW}>Overview</TabsTrigger>
                  <TabsTrigger value={TAB_DOCUMENTS}>
                    Documents ({documentsForTab.length})
                  </TabsTrigger>
                  <TabsTrigger value={TAB_BANK}>Bank Details</TabsTrigger>
                  {showInsuranceTab ? (
                    <TabsTrigger value={TAB_INSURANCE}>Insurance Details</TabsTrigger>
                  ) : null}
                </>
              )}
            </TabsList>

            <div className={styles.tabContent}>
              <TabsContent value={TAB_OVERVIEW}>
                <section className={styles.panel}>

                  <ul className={styles.summaryList}>
                    {summaryItems.length === 0 ? (
                      <li className={styles.emptyHint}>No items submitted yet.</li>
                    ) : (
                      summaryItems.map(({ key, label, status, icon: Icon }) => (
                        <li key={key} className={styles.summaryItem}>
                          <span className={styles.summaryItemLeft}>
                            <span className={styles.summaryIcon} aria-hidden>
                              <Icon size={14} strokeWidth={2} />
                            </span>
                            <span className="body-sm">{label}</span>
                          </span>
                          <span className="body-xs">{status}</span>
                        </li>
                      ))
                    )}
                  </ul>
                </section>
              </TabsContent>

              <TabsContent value={TAB_DOCUMENTS} className={styles.tabPanelDocuments}>
                {documentsForTab.length === 0 ? (
                  <p className={styles.emptyHint}>
                    No pending or rejected documents.
                  </p>
                ) : (
                  <div className={styles.documentsPanel}>
                    <div className={styles.docListScroll}>
                      <div className={styles.docList}>
                        {documentsForTab.map((doc) => {
                          const id = String(doc.documentId);
                          const result = docResults[id];
                          const path = doc.path ?? doc.documentPath;
                          const docTypeLabel = formatDocTypeLabel(doc.docType);
                          const fileName = path
                            ?.split("/")
                            .filter(Boolean)
                            .pop();
                          const viewerFileName = fileName ?? docTypeLabel;
                          const showFileName =
                            fileName != null &&
                            fileName.toLowerCase() !==
                            docTypeLabel.toLowerCase();
                          const isLoadingThis = loadingDocId === id;

                          return (
                            <article
                              key={id}
                              className={clsx(
                                styles.docRow,
                                result === "success" && styles.docRowApproved,
                                result === "failed" && styles.docRowRejected,
                              )}
                            >
                              {result === "success" ? (
                                <span className={styles.docTick} aria-hidden>
                                  <Check size={12} strokeWidth={3} />
                                </span>
                              ) : result === "failed" ? (
                                <span className={styles.docCross} aria-hidden>
                                  <X size={12} strokeWidth={3} />
                                </span>
                              ) : (
                                <span
                                  className={styles.docResultPlaceholder}
                                  aria-hidden
                                />
                              )}
                              <span className={styles.docTypeIcon} aria-hidden>
                                <FileText size={16} strokeWidth={2} />
                              </span>
                              <div className={styles.docInfo}>
                                <p className={styles.docTitle}>{docTypeLabel}</p>
                                {showFileName ? (
                                  <p
                                    className={styles.docSubtitle}
                                    title={fileName}
                                  >
                                    {fileName}
                                  </p>
                                ) : null}
                                {normalizeDocumentStatus(doc.status) === "REJECTED" ? (
                                  <p className={styles.docSubtitle}>Rejected{doc.reason ? `: ${doc.reason}` : " — correction required"}</p>
                                ) : null}
                              </div>
                              <div className={styles.docActions}>
                                {path ? (
                                  <button
                                    type="button"
                                    className={styles.viewLink}
                                    disabled={isLoadingThis || batchBusy}
                                    onClick={() =>
                                      void openFile(id, path, viewerFileName)
                                    }
                                  >
                                    {isLoadingThis ? (
                                      <span className={styles.docSpinner}>
                                        <Loader2
                                          size={14}
                                          className={styles.spin}
                                          aria-hidden
                                        />
                                      </span>
                                    ) : (
                                      <Eye size={14} aria-hidden />
                                    )}
                                    {isLoadingThis ? "Loading…" : "View"}
                                  </button>
                                ) : null}
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    </div>

                    {!isSqe && <div className={styles.docActionsBar}>
                      <Button
                        type="button"
                        variant="success"
                        disabled={busy || batchBusy || pendingDocuments.length === 0}
                        onClick={() => setConfirmAction("approveAllDocs")}
                      >
                        <Check size={14} aria-hidden />
                        Approve All
                      </Button>
                      <Button
                        type="button"
                        variant="danger"
                        disabled={busy || batchBusy || pendingDocuments.length === 0}
                        onClick={() => setConfirmAction("rejectAllDocs")}
                      >
                        <X size={14} aria-hidden />
                        Reject All
                      </Button>
                    </div>}
                  </div>
                )}
              </TabsContent>

              <TabsContent value={TAB_BANK}>
                {!row.isBankDetailsAvailable ? (
                  <p className={styles.emptyHint}>
                    Bank details have not been submitted yet.
                  </p>
                ) : (
                  <>
                    <dl className={styles.bankDl}>
                      <div className={styles.bankItem}>
                        <dt className={clsx("sub-heading-md", "text-accent")}>
                          Beneficiary Name
                        </dt>
                        <dd className="sub-heading-md ">{b.beneficiaryName ?? "—"}</dd>
                      </div>
                      <div className={styles.bankItem}>
                        <dt className={clsx("sub-heading-md", "text-accent")}>
                          Account Number
                        </dt>
                        <dd className="sub-heading-md">{b.accountNumber ?? "—"}</dd>
                      </div>
                      <div className={styles.bankItem}>
                        <dt className={clsx("sub-heading-md", "text-accent")}>
                          Bank Name
                        </dt>
                        <dd className="sub-heading-md">{b.bankName ?? "—"}</dd>
                      </div>
                      <div className={styles.bankItem}>
                        <dt className={clsx("sub-heading-md", "text-accent")}>
                          Branch Name
                        </dt>
                        <dd className="sub-heading-md">{b.branchName ?? "—"}</dd>
                      </div>
                      <div className={styles.bankItem}>
                        <dt className={clsx("sub-heading-md", "text-accent")}>
                          IFSC Code
                        </dt>
                        <dd className="sub-heading-md">{b.ifscCode ?? "—"}</dd>
                      </div>
                      <div className={styles.bankItem}>
                        <dt className={clsx("sub-heading-md", "text-accent")}>
                          Cancelled Cheque
                        </dt>
                        <dd className="sub-heading-md">
                          {b.cancelledChequePath ? (
                            <button
                              type="button"
                              className={styles.viewLink}
                              disabled={chequeLoading || batchBusy}
                              onClick={() => void openCheque()}
                            >
                              {chequeLoading ? (
                                <Loader2
                                  size={14}
                                  className={styles.spin}
                                  aria-hidden
                                />
                              ) : (
                                <Eye size={14} aria-hidden />
                              )}
                              {chequeLoading ? "Loading…" : "View"}
                            </button>
                          ) : (
                            "—"
                          )}
                        </dd>
                      </div>
                    </dl>
                  </>
                )}
              </TabsContent>

              {showInsuranceTab ? (
                <TabsContent value={TAB_INSURANCE} className={styles.insuranceTabPanel}>
                  <form
                    id="employee-insurance-form"
                    className={styles.insuranceForm}
                    onSubmit={(event) => void handleInsuranceSubmit(event)}
                    noValidate
                  >
                    <input type="hidden" name="employeeId" value={row.employeeId} />
                  

                  
                    <div className={styles.insuranceProviderSection}>

                      <TextField
                        id="insuranceProviderName"
                        name="insuranceProviderName"
                        label="Insurance Provider Name"
                        value={insuranceProviderName}
                        readOnly
                        error={insuranceErrors.insuranceProviderName}
                        disabled={insuranceBusy}
                      />
                    </div>
                    <section className={clsx(styles.insuranceEntryCard, insuranceReadOnly && styles.viewOnlyInsuranceCard)}>
                      <h3 className="heading-xs  mb-0">Self</h3>
                    <div className={styles.insuranceFields}>
                      <div className={styles.insuranceNumberRow}>
                      <TextField
                        id="insuranceNumber"
                        name="insuranceNumber"
                        label="Insurance Number"
                        required={!insuranceReadOnly}
                        maxLength={16}
                        value={insuranceNumber}
                        readOnly={insuranceReadOnly}
                        onChange={(event) => {
                          setInsuranceNumber(
                            event.target.value
                              .toUpperCase()
                              .replace(/[^A-Z0-9-]/g, "")
                              .slice(0, 16),
                          );
                          setInsuranceErrors((current) => ({
                            ...current,
                            insuranceNumber: "",
                          }));
                        }}
                        error={insuranceErrors.insuranceNumber}
                        disabled={insuranceBusy}
                        placeholder="EC2426456447-01E"
                      />
                      </div>
                      <div className={styles.validityGroup}>
                       
                        <div className={styles.validityDates}>
                          <DatePicker
                            id="insuranceFrom"
                            label="Valid From"
                            required={!insuranceReadOnly}
                            selected={parseIsoDate(insuranceFrom)}
                            onChange={(date) => {
                              const next = localIsoDate(date);
                              setInsuranceFrom(next);
                              if (insuranceTo && insuranceTo < next) setInsuranceTo("");
                              setInsuranceErrors((current) => ({
                                ...current,
                                insuranceFrom: "",
                                insuranceTo: "",
                              }));
                            }}
                            dateFormat="dd-MM-yyyy"
                            placeholderText="Valid From"
                            disabled={insuranceBusy || insuranceReadOnly}
                            customInput={<DatePickerFieldTrigger />}
                            error={insuranceErrors.insuranceFrom}
                          />
                          {/* <span className={styles.dateSeparator} aria-hidden>–</span> */}
                          <DatePicker
                            id="insuranceTo"
                            label="Valid Till"
                            minDate={parseIsoDate(insuranceFrom) ?? undefined}
                            maxDate={insuranceValidToMaxDate}
                            yearOrder="asc"
                            selected={parseIsoDate(insuranceTo)}
                            onChange={(date) => {
                              setInsuranceTo(localIsoDate(date));
                              setInsuranceErrors((current) => ({
                                ...current,
                                insuranceTo: "",
                              }));
                            }}
                            dateFormat="dd-MM-yyyy"
                            placeholderText="Valid To"
                            disabled={
                              insuranceBusy ||
                              insuranceReadOnly ||
                              !insuranceFrom
                            }
                            customInput={<DatePickerFieldTrigger />}
                            error={insuranceErrors.insuranceTo}
                          />
                        </div>
                      </div>
                    </div>

                    <div className={styles.optionalSection}>
                   
                      {!insuranceReadOnly ? (
                        <FileField
                          id="insuranceSoftCopy"
                          label="Upload Insurance"
                          accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                          stagedFile={insuranceSoftCopy}
                          onChange={(file) => {
                            setInsuranceSoftCopy(file);
                            setInsuranceErrors((current) => ({
                              ...current,
                              insuranceSoftCopy: "",
                            }));
                          }}
                          hint="PDF, JPG, JPEG, or PNG (Maximum 5 MB)"
                          error={insuranceErrors.insuranceSoftCopy}
                          disabled={insuranceBusy}
                        />
                      ) : null}
                      {existingInsurancePath ? (
                        <div className={styles.uploadedInsuranceRow}>
                          <span>Uploaded Insurance</span>
                          <button
                            type="button"
                            className={styles.viewLink}
                            disabled={insuranceBusy || loadingDocId === "insurance-soft-copy"}
                            onClick={() => void openFile("insurance-soft-copy", existingInsurancePath, existingInsuranceFileName ?? "Insurance document")}
                          >
                            {loadingDocId === "insurance-soft-copy" ? <Loader2 size={14} className={styles.spin} aria-hidden /> : <Eye size={14} aria-hidden />}
                            {loadingDocId === "insurance-soft-copy" ? "Loading…" : "View"}
                          </button>
                        </div>
                      ) : null}
                    </div>
                    </section>

                    <section className={styles.dependentInsuranceSection}>
                      <div className={styles.dependentCards}>
                        {[
                          { title: "Spouse", Icon: UsersRound, tone: styles.spouseIcon },
                          { title: "Child", Icon: Baby, tone: styles.childIcon },
                        ].filter(({ title }) => {
                          const married = String(row.maritalStatus ?? "").trim().toLowerCase() === "married";
                          return title === "Spouse" ? married : married && row.childStatus;
                        }).map(({ title, Icon, tone }) => (
                          <article className={clsx(styles.dependentCard, insuranceReadOnly && styles.viewOnlyInsuranceCard)} key={title}>
                            <div className={styles.dependentCardHeader}>
                              <div className={styles.dependentIdentity}>

                                <h4 className="heading-xs  mb-0">{title}</h4>
                              </div>
                              <Button type="button" variant="light-grey" size="sm" disabled title="Dependent API integration pending">
                                <Pencil size={14} aria-hidden /> Edit
                              </Button>
                            </div>
                            <dl className={styles.dependentDetails}>
                              <div className={styles.dependentNumber}>
                                <dt>Insurance Number</dt>
                                <dd>Not added</dd>
                              </div>
                              <div>
                                <dt>Valid From</dt>
                                <dd>—</dd>
                              </div>
                              <div>
                                <dt>Valid Till</dt>
                                <dd>—</dd>
                              </div>
                            </dl>
                            <div className={styles.dependentPolicy}>
                              <span>Upload Insurance</span>
                              <button type="button" className={styles.dependentPolicyFile} disabled>
                                <FileText size={20} aria-hidden />
                                <span><strong>No document uploaded</strong><small>PDF, JPG, JPEG, or PNG (Maximum 5 MB)</small></span>
                                <Download size={16} aria-hidden />
                              </button>
                            </div>
                            {title === "Spouse" ? (
                              <div className={styles.dependentFieldSet}>
                                <TextField id="spouseInsuranceNumber" name="spouseInsuranceNumber" label="Insurance Number" required={!insuranceReadOnly} maxLength={16} value={spouseInsuranceNumber} readOnly={insuranceReadOnly} onChange={(event) => { setSpouseInsuranceNumber(event.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "").slice(0, 16)); setInsuranceErrors((current) => ({ ...current, spouseInsuranceNumber: "" })); }} error={insuranceErrors.spouseInsuranceNumber} placeholder="EC2426456447-01E" disabled={insuranceBusy} />
                                <div className={styles.cardValidityDates}>
                                  <DatePicker id="spouseInsuranceFrom" label="Valid From" required={!insuranceReadOnly} selected={parseIsoDate(spouseInsuranceFrom)} onChange={(date) => { const next = localIsoDate(date); setSpouseInsuranceFrom(next); if (spouseInsuranceTo && spouseInsuranceTo < next) setSpouseInsuranceTo(""); setInsuranceErrors((current) => ({ ...current, spouseInsuranceFrom: "", spouseInsuranceTo: "" })); }} dateFormat="dd-MM-yyyy" placeholderText="Valid From" disabled={insuranceBusy || insuranceReadOnly} customInput={<DatePickerFieldTrigger />} error={insuranceErrors.spouseInsuranceFrom} />
                                  <DatePicker id="spouseInsuranceTo" label="Valid Till" minDate={parseIsoDate(spouseInsuranceFrom) ?? undefined} maxDate={insuranceValidToMaxDate} yearOrder="asc" selected={parseIsoDate(spouseInsuranceTo)} onChange={(date) => { setSpouseInsuranceTo(localIsoDate(date)); setInsuranceErrors((current) => ({ ...current, spouseInsuranceTo: "" })); }} dateFormat="dd-MM-yyyy" placeholderText="Valid Till" disabled={insuranceBusy || insuranceReadOnly || !spouseInsuranceFrom} customInput={<DatePickerFieldTrigger />} error={insuranceErrors.spouseInsuranceTo} />
                                </div>
                                <div className={styles.dependentUploadSection}>{insuranceReadOnly ? (spouseInsurancePath ? <div className={styles.uploadedInsuranceRow}><span>Uploaded Insurance</span><button type="button" className={styles.viewLink} disabled={loadingDocId === "spouse-insurance"} onClick={() => void openFile("spouse-insurance", spouseInsuranceDocumentPaths, spouseInsuranceFileName ?? "Spouse insurance document")}><Eye size={14} aria-hidden />{loadingDocId === "spouse-insurance" ? "Loading..." : "View"}</button></div> : <p className={styles.emptyHint}>No policy document uploaded.</p>) : <FileField id="spouseInsuranceSoftCopy" label="Upload Insurance" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" stagedFile={spouseInsuranceSoftCopy} onChange={(file) => { setSpouseInsuranceSoftCopy(file); setInsuranceErrors((current) => ({ ...current, spouseInsuranceSoftCopy: "" })); }} hint="PDF, JPG, JPEG, or PNG (Maximum 5 MB)" error={insuranceErrors.spouseInsuranceSoftCopy} disabled={insuranceBusy} />}</div>
                              </div>
                            ) : (
                              <div className={styles.dependentFieldSet}>
                                <TextField id="childInsuranceNumber" name="childInsuranceNumber" label="Insurance Number" required={!insuranceReadOnly} maxLength={16} value={childInsuranceNumber} readOnly={insuranceReadOnly} onChange={(event) => { setChildInsuranceNumber(event.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "").slice(0, 16)); setInsuranceErrors((current) => ({ ...current, childInsuranceNumber: "" })); }} error={insuranceErrors.childInsuranceNumber} placeholder="EC2426456447-01E" disabled={insuranceBusy} />
                                <div className={styles.cardValidityDates}>
                                  <DatePicker id="childInsuranceFrom" label="Valid From" required={!insuranceReadOnly} selected={parseIsoDate(childInsuranceFrom)} onChange={(date) => { const next = localIsoDate(date); setChildInsuranceFrom(next); if (childInsuranceTo && childInsuranceTo < next) setChildInsuranceTo(""); setInsuranceErrors((current) => ({ ...current, childInsuranceFrom: "", childInsuranceTo: "" })); }} dateFormat="dd-MM-yyyy" placeholderText="Valid From" disabled={insuranceBusy || insuranceReadOnly} customInput={<DatePickerFieldTrigger />} error={insuranceErrors.childInsuranceFrom} />
                                  <DatePicker id="childInsuranceTo" label="Valid Till" minDate={parseIsoDate(childInsuranceFrom) ?? undefined} maxDate={insuranceValidToMaxDate} yearOrder="asc" selected={parseIsoDate(childInsuranceTo)} onChange={(date) => { setChildInsuranceTo(localIsoDate(date)); setInsuranceErrors((current) => ({ ...current, childInsuranceTo: "" })); }} dateFormat="dd-MM-yyyy" placeholderText="Valid Till" disabled={insuranceBusy || insuranceReadOnly || !childInsuranceFrom} customInput={<DatePickerFieldTrigger />} error={insuranceErrors.childInsuranceTo} />
                                </div>
                                <div className={styles.dependentUploadSection}>{insuranceReadOnly ? (childInsurancePath ? <div className={styles.uploadedInsuranceRow}><span>Uploaded Insurance</span><button type="button" className={styles.viewLink} disabled={loadingDocId === "child-insurance"} onClick={() => void openFile("child-insurance", childInsuranceDocumentPaths, childInsuranceFileName ?? "Child insurance document")}><Eye size={14} aria-hidden />{loadingDocId === "child-insurance" ? "Loading..." : "View"}</button></div> : <p className={styles.emptyHint}>No policy document uploaded.</p>) : <FileField id="childInsuranceSoftCopy" label="Upload Insurance" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" stagedFile={childInsuranceSoftCopy} onChange={(file) => { setChildInsuranceSoftCopy(file); setInsuranceErrors((current) => ({ ...current, childInsuranceSoftCopy: "" })); }} hint="PDF, JPG, JPEG, or PNG (Maximum 5 MB)" error={insuranceErrors.childInsuranceSoftCopy} disabled={insuranceBusy} />}</div>
                              </div>
                            )}
                          </article>
                        ))}
                      </div>
                    </section>
                  </form>
                </TabsContent>
              ) : null}
            </div>
          </Tabs>
        </div>
      </Modal>

      <Modal
        open={confirmAction != null}
        onClose={() => {
          if (batchBusy || busy) return;
          setConfirmAction(null);
          setRejectReason("");
        }}
        title={confirmTitle}
        size="sm"
        closeOnBackdropClick={!batchBusy && !busy}
        closeOnEscape={!batchBusy && !busy}
        footer={
          <div className={styles.confirmFooter}>
            <Button
              type="button"
              variant="light-grey"
              disabled={batchBusy || busy}
              onClick={() => {
                setConfirmAction(null);
                setRejectReason("");
              }}
            >
              No
            </Button>
            <Button
              type="button"
              variant={confirmIsApprove ? "success" : "danger"}
              disabled={batchBusy || busy}
              onClick={() => void handleConfirmYes()}
            >
              {batchBusy ? (
                <>
                  <Loader2 size={14} className={styles.spin} aria-hidden />
                  Processing…
                </>
              ) : (
                "Yes"
              )}
            </Button>
          </div>
        }
      >
        <p className={clsx("body-sm", styles.confirmBody)}>{confirmMessage}</p>
        {showRejectReason ? (
          <TextArea
            id="ev-confirm-reason"
            className={styles.confirmReason}
            rows={3}
            placeholder="Add comment "
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            disabled={batchBusy || busy}
          />
        ) : null}
      </Modal>

      {viewerOpen && viewerUrl ? (
        <DocumentViewer
          url={viewerUrl}
          contentType={viewerType}
          fileName={viewerName}
          onClose={closeViewer}
        />
      ) : null}
    </>
  );
}
